import { useMemo, useRef, useState } from 'react'
import {
  Button,
  message,
  Space,
  Spin,
  Tooltip,
  Upload,
  UploadProps
} from 'antd'
import { Crop, Trash2, X, Check, Replace, Wand2 } from 'lucide-react'
import { useFFEStore } from '../../store/ffeStore'
import { Cropper, CropperRef, CropperState } from 'react-advanced-cropper'
import { useApi } from '../../services/ApiContext'
import 'react-advanced-cropper/dist/style.css'

export const ImageEditor = () => {
  const imageEditorEntity = useFFEStore((state) => state.imageEditorEntity)
  const setImageEditorEntity = useFFEStore(
    (state) => state.setImageEditorEntity
  )
  const lineItems = useFFEStore((state) => state.document.lineItems)
  const materials = useFFEStore((state) => state.document.materials)
  const updateLineItem = useFFEStore((state) => state.updateLineItem)
  const updateMaterial = useFFEStore((state) => state.updateMaterial)
  const [isCropping, setIsCropping] = useState(false)
  const cropperRef = useRef<CropperRef>(null)
  const api = useApi()

  const [loading, setLoading] = useState(false)
  const [bgRemovalLoading, setBgRemovalLoading] = useState(false)
  const [bgRemovalPreviewUrl, setBgRemovalPreviewUrl] = useState<string | null>(
    null
  )

  const currentLineItem = useMemo(
    () => lineItems.find((item) => item.id === imageEditorEntity?.id),
    [lineItems, imageEditorEntity]
  )
  const currentMaterial = useMemo(() => {
    if (!imageEditorEntity?.materialId) return undefined
    return materials.find(
      (material) => material.id === imageEditorEntity?.materialId
    )
  }, [materials, imageEditorEntity])

  const image = useMemo(() => {
    if (imageEditorEntity?.type === 'lineItem') {
      return currentLineItem?.image
    }
    if (imageEditorEntity?.type === 'material') {
      return currentMaterial?.image
    }
  }, [currentLineItem, currentMaterial, imageEditorEntity])

  const onImageChange = (image: { url: string; alt: string } | undefined) => {
    if (!imageEditorEntity) return
    if (imageEditorEntity.type === 'lineItem') {
      updateLineItem(imageEditorEntity.id, { image })
    }
    if (imageEditorEntity.type === 'material' && imageEditorEntity.materialId) {
      updateMaterial(imageEditorEntity.materialId, { image })
    }
  }

  const onDelete = () => {
    onImageChange(undefined)
    onClose()
  }

  const onClose = () => {
    setBgRemovalPreviewUrl(null)
    setImageEditorEntity(undefined)
  }

  const displayUrl = bgRemovalPreviewUrl ?? image?.url

  const onRemoveBackground = async () => {
    if (!image?.url) {
      return
    }
    setBgRemovalLoading(true)
    try {
      const result = await api.removeImageBackground(image.url)
      setBgRemovalPreviewUrl(result.url)
    } catch {
      message.error('Could not remove background')
    } finally {
      setBgRemovalLoading(false)
    }
  }

  const onCancelBgRemoval = () => {
    setBgRemovalPreviewUrl(null)
  }

  const onConfirmBgRemoval = () => {
    if (!bgRemovalPreviewUrl || !image) return
    onImageChange({
      url: bgRemovalPreviewUrl,
      alt: image.alt
    })
    setBgRemovalPreviewUrl(null)
  }

  const onStartCrop = () => {
    setIsCropping(true)
  }

  const onFinishCrop = async () => {
    if (!cropperRef.current || !image) {
      return
    }
    setLoading(true)
    // setCoordinates(cropperRef.current.getCoordinates())
    // You are able to do different manipulations at a canvas
    // but there we just get a cropped image, that can be used
    // as src for <img/> to preview result

    const src = cropperRef.current.getCanvas()?.toDataURL()
    if (!src) {
      return
    }
    const blob = await fetch(src).then((res) => res.blob())
    const file = new File([blob], image.alt, { type: blob.type })
    console.log(file)
    const response = await api.uploadImage(file)
    onImageChange({
      url: response,
      alt: image.alt
    })
    setIsCropping(false)
    setLoading(false)
  }

  // Find current image index in related images
  //   const relatedImages: Array<string> = []
  //   useEffect(() => {
  //     const index = relatedImages.findIndex((img) => img === imageUrl)
  //     if (index !== -1) {
  //       setCurrentImageIndex(index)
  //     }
  //   }, [imageUrl, relatedImages])

  //   const handlePrevImage = () => {
  //     if (currentImageIndex > 0) {
  //       const newIndex = currentImageIndex - 1
  //       setCurrentImageIndex(newIndex)
  //       onImageChange?.(relatedImages[newIndex], relatedImages[newIndex])
  //     }
  //   }

  //   const handleNextImage = () => {
  //     if (currentImageIndex < relatedImages.length - 1) {
  //       const newIndex = currentImageIndex + 1
  //       setCurrentImageIndex(newIndex)
  //       onImageChange?.(relatedImages[newIndex], relatedImages[newIndex])
  //     }
  //   }

  //   const handleThumbnailClick = (index: number) => {
  //     setCurrentImageIndex(index)
  //     onImageChange?.(relatedImages[index], relatedImages[index])
  //   }

  const defaultSize = ({ imageSize, visibleArea }: CropperState) => {
    const smallerSide = Math.min(
      visibleArea?.width || imageSize.width,
      visibleArea?.height || imageSize.height
    )

    return {
      width: smallerSide,
      height: smallerSide
    }
  }

  const handleUpload: UploadProps['customRequest'] = async ({
    file,
    onSuccess
  }) => {
    try {
      setLoading(true)

      if (!(file instanceof File)) {
        throw new Error('Invalid file')
      }

      // Create object URL for preview
      const objectUrl = await api.uploadImage(file)

      onImageChange({
        url: objectUrl,
        alt: file.name
      })

      onSuccess?.(null)
    } catch {
      message.error('Failed to upload image')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative h-[80vh] flex flex-col bg-white">
      {/* Top toolbar */}
      <div className="absolute top-0 left-0 right-0 p-4 z-10">
        <div className="flex justify-between items-center">
          <div /> {/* Empty div for spacing */}
          <Space>
            {!isCropping && !bgRemovalPreviewUrl && (
              <>
                <Tooltip title="Crop image">
                  <Button
                    onClick={onStartCrop}
                    icon={<Crop className="h-4 w-4" />}
                  />
                </Tooltip>
                <Tooltip title="Remove background">
                  <Button
                    onClick={onRemoveBackground}
                    loading={bgRemovalLoading}
                    disabled={!image?.url || bgRemovalLoading}
                    icon={<Wand2 className="h-4 w-4" />}
                  />
                </Tooltip>
                <Tooltip title="Replace image">
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    customRequest={handleUpload}
                    className="w-[70px] h-[70px] flex-shrink-0"
                  >
                    <Button icon={<Replace className="h-4 w-4" />} />
                  </Upload>
                </Tooltip>
                <Tooltip title="Delete image">
                  <Button
                    danger
                    icon={<Trash2 className="h-4 w-4" />}
                    onClick={onDelete}
                  />
                </Tooltip>
                <Tooltip title="Close">
                  <Button icon={<X className="h-4 w-4" />} onClick={onClose} />
                </Tooltip>
              </>
            )}
            {!isCropping && bgRemovalPreviewUrl && (
              <>
                <Button icon={<X className="h-4 w-4" />} onClick={onCancelBgRemoval}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  icon={<Check className="h-4 w-4" />}
                  onClick={onConfirmBgRemoval}
                >
                  Confirm
                </Button>
              </>
            )}
            {isCropping && (
              <>
                <Button
                  icon={<X className="h-4 w-4" />}
                  onClick={() => setIsCropping(false)}
                >
                  Cancel crop
                </Button>
                <Button
                  icon={<Check className="h-4 w-4" />}
                  onClick={onFinishCrop}
                >
                  Finish crop
                </Button>
              </>
            )}
          </Space>
        </div>
      </div>

      {/* Main image */}
      <div className="flex-1 flex items-center justify-center relative">
        <Spin spinning={loading || bgRemovalLoading}>
          {isCropping && (
            <div>
              <Cropper
                className="p-8 !bg-white max-h-[65vh] max-w-[55vw]"
                // defaultPosition={defaultPosition}
                defaultSize={defaultSize}
                ref={cropperRef}
                stencilProps={{
                  grid: true,
                  handlerClassNames: {
                    default: 'text-gray-300'
                  }
                }}
                src={image?.url}
              />
            </div>
          )}
          {!isCropping && (
            <img
              src={displayUrl}
              alt={image?.alt}
              className="min-h-[45vh] min-w-[45vh]  max-h-[65vh] max-w-[55vw] object-contain"
            />
          )}
        </Spin>

        {/* Navigation arrows */}
        {/* {relatedImages.length > 1 && (
          <>
          <Button
          className="absolute left-4 top-1/2 -translate-y-1/2"
          icon={<ChevronLeft className="h-5 w-5" />}
          onClick={handlePrevImage}
          disabled={currentImageIndex === 0}
          />
          <Button
          className="absolute right-4 top-1/2 -translate-y-1/2"
              icon={<ChevronRight className="h-5 w-5" />}
              onClick={handleNextImage}
              disabled={currentImageIndex === relatedImages.length - 1}
            />
          </>
        )} */}
      </div>

      {/* Thumbnails */}
      {/* {relatedImages.length > 0 && (
        <div className="p-4 bg-black/50">
          <div className="flex gap-2 overflow-x-auto pb-2">
          {relatedImages.map((image, index) => (
            <div
            key={image.url}
            className={`
            w-20 h-20 flex-shrink-0 cursor-pointer rounded-lg overflow-hidden
            border-2 transition-colors
            ${index === currentImageIndex ? 'border-primary' : 'border-transparent'}
            `}
            onClick={() => handleThumbnailClick(index)}
            >
            <img
            src={image.url}
            alt={image.alt}
            className="w-full h-full object-cover"
            />
            </div>
            ))}
            </div>
            </div>
            )} */}
    </div>
  )
}
